import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { useWallet } from "@/context/WalletContext";
import { createApiService } from "@/App";
import { toast } from "sonner";
import Header from "@/components/Header";
import {
  Zap,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Target,
  Gauge,
  Activity,
  RefreshCw,
  Wallet,
  Shield,
  AlertTriangle
} from "lucide-react";

const strategies = [
  {
    id: "hybrid",
    name: "Hybrid (Recommended)",
    description: "Combines all strategies. Picks the strongest signal.",
    icon: <Activity className="w-5 h-5" />,
    risk: "Medium",
    riskColor: "text-yellow-400"
  },
  {
    id: "momentum",
    name: "Momentum",
    description: "Follow strong price trends. Good for trending markets.",
    icon: <TrendingUp className="w-5 h-5" />,
    risk: "Medium",
    riskColor: "text-yellow-400"
  },
  {
    id: "mean_reversion",
    name: "Mean Reversion",
    description: "Fade extreme moves. Buy dips, sell spikes.",
    icon: <RefreshCw className="w-5 h-5" />,
    risk: "Medium",
    riskColor: "text-yellow-400"
  },
  {
    id: "latency_arb",
    name: "Latency Arbitrage",
    description: "React to fast price movements. High frequency.",
    icon: <Zap className="w-5 h-5" />,
    risk: "High",
    riskColor: "text-red-400"
  }
];

export default function ArbitrageBotCreate() {
  const navigate = useNavigate();
  const { isConnected, connectWallet, getAuthHeaders, addresses, signContractCall } = useWallet();
  const apiService = createApiService(getAuthHeaders);

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  // Bot configuration
  const [botName, setBotName] = useState("");  // NEW: Bot name (2 characters like agents)
  const [selectedStrategy, setSelectedStrategy] = useState("hybrid");
  const [killSwitchPct, setKillSwitchPct] = useState(-5);
  const [takeProfitPct, setTakeProfitPct] = useState(10);
  const [initialDeposit, setInitialDeposit] = useState("");

  const handleCreateBot = async () => {
    if (!isConnected) {
      toast.error("Please connect your wallet first");
      connectWallet();
      return;
    }

    // Validate bot name
    if (!botName || botName.length < 2) {
      toast.error("Bot name must be at least 2 characters");
      setStep(1);
      return;
    }

    const depositAmount = initialDeposit ? parseFloat(initialDeposit) : 0;
    
    setLoading(true);
    try {
      // Step 1: Create the bot first
      const response = await apiService.post("/prediction-bots", {
        name: botName.toUpperCase(),  // Include bot name
        strategy: selectedStrategy,
        kill_switch_pct: killSwitchPct,
        take_profit_pct: takeProfitPct,
        initial_deposit_stxoshi: 0  // We'll deposit after wallet approval
      });

      if (!response.data.success) {
        throw new Error("Failed to create bot");
      }

      const botId = response.data.bot.id;
      
      // Step 2: If user entered a deposit amount, trigger wallet transaction
      if (depositAmount > 0) {
        toast.info("Bot created! Now approve the STXOSHI deposit in your wallet...");
        
        // Initiate the deposit
        const initResponse = await apiService.post(`/prediction-bots/${botId}/deposit/initiate`, {
          amount: depositAmount
        });

        if (!initResponse.data.success) {
          toast.warning("Bot created but deposit initiation failed. You can deposit from the dashboard.");
          navigate(`/bot/${botId}`);
          return;
        }

        const { deposit_id, hot_wallet, stxoshi_contract, amount_stxoshi } = initResponse.data;
        const [contractAddress, contractName] = stxoshi_contract.split(".");

        // Import Clarity value constructors
        const { uintCV, principalCV, noneCV } = await import("@stacks/transactions");

        // Call the wallet to sign the transfer
        const txResult = await signContractCall({
          contractAddress,
          contractName,
          functionName: "transfer",
          functionArgs: [
            uintCV(amount_stxoshi),
            principalCV(addresses.stx),
            principalCV(hot_wallet),
            noneCV()
          ],
          postConditions: [],
          postConditionMode: "allow"
        });

        if (txResult && txResult.txid) {
          toast.success("Transaction submitted! Confirming deposit...");
          
          // Wait for transaction to propagate
          await new Promise(resolve => setTimeout(resolve, 3000));

          // Confirm the deposit
          try {
            const confirmResponse = await apiService.post(`/prediction-bots/${botId}/deposit/confirm`, {
              deposit_id,
              txid: txResult.txid
            });

            if (confirmResponse.data.success) {
              toast.success(`Deposited ${depositAmount.toLocaleString()} STXOSHI to bot!`);
            } else {
              toast.info("Deposit pending confirmation. Check the bot dashboard.");
            }
          } catch (confirmErr) {
            toast.info("Deposit transaction submitted. Confirmation pending on blockchain.");
          }
        } else {
          toast.warning("Bot created but deposit was cancelled. You can deposit from the dashboard.");
        }
      } else {
        toast.success("Arbitrage Bot created successfully!");
      }

      navigate(`/bot/${botId}`);
    } catch (err) {
      console.error("Failed to create bot:", err);
      if (err.message?.includes("cancelled") || err.message?.includes("rejected")) {
        toast.error("Wallet transaction cancelled");
      } else {
        toast.error(err.response?.data?.detail || "Failed to create bot");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="p-6">
          <Card className="glass-card max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <Wallet className="w-12 h-12 text-primary mx-auto mb-4" />
              <h2 className="font-heading text-xl font-semibold mb-2">Connect Wallet</h2>
              <p className="text-muted-foreground mb-4">Connect your wallet to create an Arbitrage Bot</p>
              <Button onClick={connectWallet} className="bg-primary">
                <Wallet className="w-4 h-4 mr-2" />
                Connect Wallet
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="p-4 md:p-6">
        <div className="max-w-2xl mx-auto">
          {/* Back Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/launch")}
            className="mb-6 text-muted-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Agent Types
          </Button>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-secondary/20 flex items-center justify-center mx-auto mb-4">
              <Zap className="w-8 h-8 text-secondary" />
            </div>
            <h1 className="font-heading text-3xl font-bold mb-2">Arbitrage Prediction Bot Hunter</h1>
            <p className="text-muted-foreground">
              Automated quantitative trading with kill switch protection
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-8">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <button
                  onClick={() => {
                    // Only allow going back or to completed steps
                    if (s < step || (s === 2 && botName.length >= 2)) {
                      setStep(s);
                    }
                  }}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-mono text-sm transition-all ${
                    step === s
                      ? 'bg-secondary text-secondary-foreground'
                      : step > s
                        ? 'bg-secondary/20 text-secondary'
                        : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {s}
                </button>
                {s < 4 && (
                  <div className={`w-12 h-0.5 mx-1 ${step > s ? 'bg-secondary' : 'bg-border'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Name Your Bot */}
          {step === 1 && (
            <div className="animate-fade-in">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-secondary" />
                    Name Your Bot
                  </CardTitle>
                  <CardDescription>
                    Give your bot a short identifier (2 characters like agents)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Bot Name</Label>
                    <Input
                      value={botName}
                      onChange={(e) => setBotName(e.target.value.toUpperCase().slice(0, 4))}
                      placeholder="e.g., AB, QT, X1"
                      maxLength={4}
                      className="text-2xl font-mono text-center h-16 tracking-widest"
                      data-testid="bot-name-input"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      2-4 characters. This will be your bot's identifier.
                    </p>
                  </div>

                  <Button
                    onClick={() => setStep(2)}
                    disabled={botName.length < 2}
                    className="w-full bg-secondary hover:bg-secondary/90 h-12 mt-4"
                    data-testid="name-continue-btn"
                  >
                    Continue
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 2: Select Strategy */}
          {step === 2 && (
            <div className="animate-fade-in">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-secondary" />
                    Select Trading Strategy
                  </CardTitle>
                  <CardDescription>
                    Choose how your bot analyzes markets and enters trades
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {strategies.map((strategy) => (
                    <button
                      key={strategy.id}
                      onClick={() => setSelectedStrategy(strategy.id)}
                      className={`w-full p-4 rounded-xl border text-left transition-all flex items-start gap-4 ${
                        selectedStrategy === strategy.id
                          ? 'border-secondary bg-secondary/10'
                          : 'border-border hover:border-secondary/50'
                      }`}
                      data-testid={`strategy-${strategy.id}`}
                    >
                      <div className={`text-secondary ${selectedStrategy === strategy.id ? 'opacity-100' : 'opacity-60'}`}>
                        {strategy.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">{strategy.name}</p>
                          <Badge variant="outline" className={strategy.riskColor}>
                            {strategy.risk} Risk
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {strategy.description}
                        </p>
                      </div>
                    </button>
                  ))}

                  <Button
                    onClick={() => setStep(3)}
                    className="w-full bg-secondary hover:bg-secondary/90 h-12 mt-4"
                  >
                    Continue
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 3: Risk Parameters */}
          {step === 3 && (
            <div className="animate-fade-in">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-secondary" />
                    Risk Management
                  </CardTitle>
                  <CardDescription>
                    Set your kill switch (stop loss) and take profit thresholds
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                  {/* Kill Switch */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <TrendingDown className="w-5 h-5 text-red-400" />
                        <Label className="text-base font-medium">Kill Switch (Stop Loss)</Label>
                      </div>
                      <span className="font-mono text-xl text-red-400">{killSwitchPct}%</span>
                    </div>
                    <Slider
                      value={[Math.abs(killSwitchPct)]}
                      onValueChange={([v]) => setKillSwitchPct(-v)}
                      min={1}
                      max={50}
                      step={1}
                      className="w-full"
                    />
                    <p className="text-sm text-muted-foreground mt-2">
                      Bot will stop all trading and close positions if total P&L drops to {killSwitchPct}%
                    </p>
                  </div>

                  {/* Take Profit */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-400" />
                        <Label className="text-base font-medium">Take Profit</Label>
                      </div>
                      <span className="font-mono text-xl text-green-400">+{takeProfitPct}%</span>
                    </div>
                    <Slider
                      value={[takeProfitPct]}
                      onValueChange={([v]) => setTakeProfitPct(v)}
                      min={1}
                      max={100}
                      step={1}
                      className="w-full"
                    />
                    <p className="text-sm text-muted-foreground mt-2">
                      Bot will close all positions and lock in profit when P&L reaches +{takeProfitPct}%
                    </p>
                  </div>

                  {/* Preview Card */}
                  <Card className="bg-muted/30 border-border">
                    <CardContent className="p-4">
                      <h4 className="font-medium mb-3">Risk/Reward Preview</h4>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Risk</span>
                        <span className="text-red-400">{killSwitchPct}%</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-2">
                        <span className="text-muted-foreground">Reward</span>
                        <span className="text-green-400">+{takeProfitPct}%</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-2">
                        <span className="text-muted-foreground">Ratio</span>
                        <span className="text-primary">
                          1:{(takeProfitPct / Math.abs(killSwitchPct)).toFixed(1)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex gap-4">
                    <Button
                      variant="outline"
                      onClick={() => setStep(2)}
                      className="flex-1 h-12"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={() => setStep(4)}
                      className="flex-1 bg-secondary hover:bg-secondary/90 h-12"
                    >
                      Continue
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 4: Review & Deploy */}
          {step === 4 && (
            <div className="animate-fade-in">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-secondary" />
                    Review & Deploy
                  </CardTitle>
                  <CardDescription>
                    Confirm your bot configuration and deploy
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Summary */}
                  <Card className="bg-muted/30 border-border">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Bot Name</span>
                        <span className="font-mono font-bold text-secondary">{botName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Strategy</span>
                        <span className="font-medium capitalize">{selectedStrategy.replace("_", " ")}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Kill Switch</span>
                        <span className="text-red-400">{killSwitchPct}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Take Profit</span>
                        <span className="text-green-400">+{takeProfitPct}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Wallet</span>
                        <span className="font-mono text-sm">{addresses.stx?.slice(0, 8)}...{addresses.stx?.slice(-6)}</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Initial Deposit */}
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">
                      Initial STXOSHI Deposit
                    </Label>
                    <Input
                      type="number"
                      placeholder="e.g., 1000000"
                      value={initialDeposit}
                      onChange={(e) => setInitialDeposit(e.target.value)}
                      className="bg-muted/50 border-border"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter amount to deposit. Your wallet will prompt for approval.
                    </p>
                  </div>

                  {/* Warning */}
                  <Card className="bg-yellow-500/10 border-yellow-500/30">
                    <CardContent className="p-4 flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-yellow-500 font-medium">Risk Warning</p>
                        <p className="text-xs text-yellow-500/80 mt-1">
                          Automated trading involves risk. The kill switch helps limit losses but cannot guarantee protection against all market conditions.
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex gap-4">
                    <Button
                      variant="outline"
                      onClick={() => setStep(3)}
                      className="flex-1 h-12"
                      disabled={loading}
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleCreateBot}
                      disabled={loading}
                      className="flex-1 bg-secondary hover:bg-secondary/90 h-12"
                      data-testid="deploy-bot-btn"
                    >
                      {loading ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Deploying...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          Deploy Arbitrage Bot
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      <div className="h-20" />
    </div>
  );
}
